import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert
} from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { resetPassword, User } from '../../api/admin';

interface PasswordResetDialogProps {
  open: boolean;
  user: User | null;
  onClose: (refresh?: boolean) => void;
}

const PasswordResetDialog: React.FC<PasswordResetDialogProps> = ({ open, user, onClose }) => {
  const { t } = useLanguage();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
  }, [user]);

  const handleSubmit = async () => {
    if (!user || !newPassword) {
      setError(t('admin.users.validation.required'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('admin.users.validation.passwordMismatch'));
      return;
    }

    if (newPassword.length < 8) {
      setError(t('admin.users.validation.passwordTooShort'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await resetPassword(user.username, newPassword);
      onClose(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{t('admin.users.actions.password')} - {user?.username}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          label={t('admin.users.newPassword')}
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          label={t('admin.users.confirmPassword')}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>{t('common.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {t('common.reset')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordResetDialog;
